// Vollea — Emergent Google OAuth callback handler.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// Emergent sends the user back to {redirect_url}#session_id=<uuid>. We read it
// from window.location.hash exactly once, exchange it via POST /api/auth/session,
// then route to the right home (admin vs dashboard). Outstanding invite codes
// (closed-beta gating) are pulled from sessionStorage so first-time Google sign-ups
// can complete onboarding without losing the code across the redirect bounce.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "./ui-vollea";
import { C, SP } from "../lib/theme";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    // useRef guard prevents StrictMode from double-invoking the exchange.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    const inviteCode = sessionStorage.getItem("vollea_invite_code") || undefined;

    (async () => {
      try {
        const user = await loginGoogle(sessionId, inviteCode);
        sessionStorage.removeItem("vollea_invite_code");
        // Clear the fragment so a refresh doesn't try to re-exchange the (now-spent) id.
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate(user.role === "super_admin" ? "/admin" : "/dashboard", { replace: true });
      } catch (e) {
        const msg = e?.message || "Sign-in failed";
        if (/invite_required|invite/i.test(msg)) {
          // New Google user needs an invite during closed beta — bounce to register
          // where they can paste it; preserve the session_id in case the user has one.
          sessionStorage.setItem("vollea_pending_session_id", sessionId);
          navigate("/register?google=1", { replace: true });
          return;
        }
        setErr(msg);
      }
    })();
  }, [loginGoogle, navigate]);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: SP.lg, padding: SP.lg }}>
      <Logo size={28} />
      {err ? (
        <>
          <div data-testid="auth-callback-error" style={{ color: C.error, fontSize: 14, textAlign: "center", maxWidth: 360 }}>{err}</div>
          <button data-testid="auth-callback-back" onClick={() => navigate("/login", { replace: true })} style={{ color: C.lime, fontSize: 13, fontWeight: 600 }}>Back to login</button>
        </>
      ) : (
        <div data-testid="auth-callback-loading" style={{ color: C.textMuted, fontSize: 13 }}>Signing you in…</div>
      )}
    </div>
  );
}
